import {map} from 'lodash';
import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';

/*
 * Props.
 */

export interface FormattingFontSizeProps {
  fontSize?: number;
  onFontSizeSelect: HandlerOf<number>;
  isDisabled?: boolean;
}

/*
 * Constants.
 */

const dropdownMaxHeight = 200;

const indeterminateValue: ReactNode = '—';

const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 36, 40];
const presets: ReadonlyArray<FontSizePreset> = fontSizes.map((fontSize) => ({
  value: fontSize.toFixed(0),
  fontSize,
}));

/*
 * Types.
 */

type FontSizePreset = {
  value: ReactNode;
  fontSize: number;
};

/*
 * Styles.
 */

// Makes the numbers line up, especially between regular/bold and Segoe UI.
const StyledSimpleDropdown = styled(SimpleDropdown)`
  font-variant-numeric: tabular-nums;
`;

/*
 * Component.
 */

export const FormattingFontSize: FC<FormattingFontSizeProps> = (props) => {
  // Gather the font size options.
  const possiblePresets = presets;
  const possibleValues = useMemo(() => possiblePresets.map((preset) => preset.value), [possiblePresets]);

  // Map the numeric font size to the possible options.
  const selectedFontSize = props.fontSize && Math.round(props.fontSize);
  const selectedPreset =
    selectedFontSize && possiblePresets.find((preset) => preset.fontSize === selectedFontSize);
  const selectedValue = selectedPreset ? selectedPreset.value : indeterminateValue;

  const onFontSizeSelect: HandlerOf<number> = (fontSize) => {
    props.onFontSizeSelect(fontSize);
    trackInterfaceEvent(InterfaceEventsEnum.SELECT_FONT_SIZE, {
      font_size: fontSize,
    });
  };

  return (
    <DropdownBoxCoordinator
      value={selectedValue}
      possibleValues={possibleValues}
      size={VisualSizesEnum.SMALL}
      isDisabled={props.isDisabled}
      renderDropdown={(modalProps) =>
        renderDropdown(modalProps, possiblePresets, selectedFontSize, onFontSizeSelect)
      }
      renderTooltip={() => <Trans id="formatting-fontsize">Font size</Trans>}
      shouldPreventMouseDown
      interactionId="formatting-fontsize"
    />
  );
};

/*
 * Dropdown.
 */

/* eslint react/no-array-index-key: 0 */
function renderDropdown(
  modalProps: PopoverTransferProps,
  possiblePresets: ReadonlyArray<FontSizePreset>,
  selectedFontSize: number | undefined,
  onFontSizeSelect: HandlerOf<number>,
) {
  const selectedIndex = possiblePresets.findIndex((preset) => preset.fontSize === selectedFontSize);
  const dropdownItems = map(possiblePresets, (preset) => (
    <SimpleDropdownItem
      key={String(preset.fontSize)}
      isChecked={preset.fontSize === selectedFontSize}
      onSelect={() => onFontSizeSelect(preset.fontSize)}
      interactionId={String(preset.fontSize)}
    >
      {preset.value}
    </SimpleDropdownItem>
  ));

  return (
    <StyledSimpleDropdown
      {...modalProps}
      maxHeight={dropdownMaxHeight}
      defaultHighlightedIndex={selectedIndex}
      shouldCenterHighlightedItem
      interactionId="formatting-fontsize-dropdown"
    >
      {dropdownItems}
    </StyledSimpleDropdown>
  );
}
